// Strings of the network page: header, banners, tabs, Overview, Attachments, Attach deployments…, Apply dialog, Runs
// and Activity tabs (spec 11 6.15.4–6.15.7, 6.15.11, 6.15.13). `strings.networks.page`.
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkPage = {
  // Header (6.15.4)
  contextApplied: (when: string, by: string | null) => `applied ${when}${by ? ` by ${by}` : ''}`,
  notApplied: 'not applied yet',
  attachments: (n: number) => plural(n, 'attachment'),
  plan: 'Plan',
  apply: 'Apply…',
  refresh: 'Refresh',
  nothingToRefresh: 'Nothing to refresh yet.',
  moreActions: (name: string) => `More actions for ${name}`,
  gettingStarted: 'Getting started',
  destroy: 'Destroy…',
  delete: 'Delete',
  runQueued: (network: string, kind: string, by: string) =>
    `${network}’s ${kind} by ${by} is queued; plan after it finishes.`,
  runRunning: (network: string, kind: string, by: string) =>
    `${network}’s ${kind} by ${by} is running; plan after it finishes.`,
  runActive: 'A run of this network is active.',
  // Run banner
  runBanner: (label: string) => `${label} running`,
  runStarted: (when: string, by: string) => `started ${when} by ${by}`,
  openRun: 'Open run',
  cancel: 'Cancel…',
  runsQueued: (n: number) => (n === 1 ? '1 run queued' : `${n} runs queued`),
  banners: 'Notices',
  // Destroy and Delete
  destroyTitle: (network: string) => `Destroy ${network}`,
  destroyText: (network: string) =>
    `This deletes everything ${network} created in the cloud: its private DNS zones and their links and records. Its addresses stay reserved: applying it again re-creates it with the same addresses.`,
  destroyDetachFirst: (n: number) =>
    `Detach the ${n === 1 ? 'attached deployment' : `${n} attached deployments`} first.`,
  nothingToDestroy: 'Nothing to destroy.',
  destroySubmit: (network: string) => `Destroy ${network}`,
  typeToConfirm: (network: string) => `Type “${network}” to confirm.`,
  deleteTitle: (network: string) => `Delete ${network}?`,
  deleteText: (network: string) =>
    `${network} and its address plan are deleted. Nothing in the cloud changes: the network was never applied or is destroyed.`,
  deleteOnlyNew: 'Only a new or destroyed network without attachments can be deleted.',
  deleted: (network: string) => `${network} was deleted.`,
  // Banners
  lastRunFailed: (kind: string, network: string) => `The last ${kind} of ${network} failed:`,
  refreshFirst: 'Refresh before the next apply.',
  destroyed: (network: string) =>
    `${network} is destroyed. Apply it to re-create it with the same addresses.`,
  failedAttachments: (n: number) =>
    n === 1 ? '1 attachment failed to connect.' : `${n} attachments failed to connect.`,
  show: 'Show',
  settingsChanged: 'Settings changed since the last apply.',
  waitForApply: (n: number) =>
    n === 1
      ? '1 attachment waits for the network’s apply.'
      : `${n} attachments wait for the network’s apply.`,
  // Tabs
  tabs: 'Network sections',
  tab: {
    overview: 'Overview',
    attachments: 'Attachments',
    'address-plan': 'Address plan',
    dns: 'Private DNS',
    'entry-points': 'Entry points',
    traffic: 'Traffic',
    runs: 'Runs',
    settings: 'Settings',
    activity: 'Activity',
  },
  // Getting started (6.15.5)
  checklist: 'Getting started',
  checklistLabel: (network: string) => `Getting started with ${network}`,
  done: 'Done',
  notDone: 'Not done yet',
  comingLater: 'coming later',
  step1: 'Plan and apply the network',
  step1Done: (when: string) => `Applied ${when}`,
  step2: 'Attach deployments',
  step3: 'Apply each attached deployment',
  step3Text:
    'Attaching re-creates a deployment’s network: its private connections pause until Nebisys has connected it.',
  step4: 'Later: entry points, micro-segmentation and network requests',
  closeChecklist: 'Close',
  planNetwork: 'Plan network',
  attachDeployments: 'Attach deployments…',
  // Topology (6.15.5)
  topology: 'Topology',
  diagram: 'Diagram',
  list: 'List',
  view: 'View',
  legend: 'Legend',
  treeLabel: (network: string) => `Topology of ${network}`,
  entryPoints: 'Entry points',
  noEntryPoints: 'No entry points.',
  privateDns: 'Private DNS',
  dnsSummary: (zones: number, records: number, internal: string | null) =>
    `${plural(zones, 'zone')} · ${plural(records, 'record')}${internal ? ` · internal zone ${internal}` : ''}`,
  writtenByNetwork: 'Written by the network only',
  providerName: (provider: string, regions: number) => `${provider}, ${plural(regions, 'region')}`,
  regionName: (region: string, blocks: string, count: number) =>
    `${region}${blocks ? `, ${blocks}` : ''}, ${plural(count, 'attachment')}`,
  attachmentName: (parts: string[]) => parts.join(', '),
  noAttachmentsInRegion: 'No attachments yet.',
  more: (n: number, summary: string) => `+${n} more · ${summary}`,
  moreName: (n: number, summary: string) => `${n} more attachments, ${summary}`,
  allState: (state: string) => `all ${state.toLowerCase()}`,
  stateCount: (n: number, state: string) => `${n} ${state.toLowerCase()}`,
  cardState: {
    connected: (connectivity: string) => `Connected · ${connectivity}`,
    connecting: 'Connecting',
    pending: 'Pending · apply it first',
    waits: 'Waits for the network’s apply',
    failed: 'Failed',
    detaching: 'Detaching',
    detached: 'Detached',
    'spoke-ready': 'Connecting',
  },
  // Side panel
  details: (item: string) => `${item} details`,
  close: 'Close',
  attachmentContext: (provider: string, region: string, blueprint: string) =>
    `Attachment · ${provider} · ${region} · blueprint ${blueprint}`,
  lastConnect: (when: string) => `connect run ${when}`,
  open: 'open',
  addresses: 'Addresses',
  block: 'Block',
  connectivity: 'Connectivity',
  connectivityText: (connectivity: string) =>
    connectivity === 'isolated'
      ? 'Isolated: other attachments can’t reach it. It gets its addresses from the plan and the network’s private DNS.'
      : connectivity,
  grant: 'Grant',
  grantAzure: 'Network Contributor on the spoke’s resource group',
  grantAws:
    'IAM role nebisys-net-<network>-<deployment> in the deployment’s account: routes, security group rules, the transit gateway attachment and the private DNS association of its VPC',
  awsZones: (a: string, b: string) => `a ${a} · b ${b}`,
  openDeployment: 'Open deployment',
  retry: 'Retry',
  regionPanel: (blocks: string, count: number) => `Region blocks ${blocks} · ${plural(count, 'attachment')}`,
  showDns: 'Open Private DNS',
  showEntryPoints: 'Open Entry points',
  // Attachments (6.15.7)
  filters: 'Filters',
  state: 'State',
  provider: 'Provider',
  region: 'Region',
  search: 'Filter by deployment',
  columns: {
    deployment: 'Deployment',
    blueprint: 'Blueprint',
    providerRegion: 'Provider / region',
    cidr: 'CIDR',
    size: 'Size',
    state: 'State',
    connectivity: 'Connectivity',
    requests: 'Requests',
    lastConnect: 'Last connect',
    actions: 'Actions',
  },
  copyCidr: (cidr: string) => `Copy ${cidr}`,
  noAttachments: 'No deployment is attached yet.',
  noMatch: 'No attachment matches the filters.',
  attachTitle: (network: string) => `Attach deployments to ${network}`,
  attachPolicyOnlyAdmins: (network: string) => `Only network admins attach to ${network}`,
  requiresOperator: 'Requires the operator role.',
  // Attach deployments… (6.15.6)
  attachEmpty: (network: string) =>
    `No deployment can attach yet: a deployment needs an account ${network} allows and a region it enables.`,
  attachSubmit: (n: number) => `Attach ${n}`,
  attachSelect: (deployment: string) => `Attach ${deployment}`,
  sizeFor: (deployment: string) => `Size for ${deployment}`,
  assignedWhenSaved: (prefix: number) => `a /${prefix} block, assigned when saved`,
  blockLine: (provider: string, region: string, cidr: string) => `${provider} · ${region} ${cidr}`,
  attached: 'Attached',
  attaching: 'Attaching…',
  pendingApply: 'Pending: apply',
  pendingApplyEnd: 'to connect them.',
  eligibleLabel: 'Deployments that can attach',
  // Apply dialog (6.15.11)
  applyTitle: (network: string) => `Apply ${network}`,
  applyPlan: 'Apply this plan',
  applyPlanSummary: (time: string, by: string, changes: string) => `Plan from ${time} by ${by}: ${changes}`,
  applyWithout: 'Apply without a reviewed plan',
  applyWithoutHelp: 'Nebisys plans and applies in one run.',
  noCurrentPlan: 'No current plan: plan the network to review its changes first.',
  dangerous: 'Dangerous changes',
  recreated: (component: string) => `${component} is re-created: traffic through it stops briefly.`,
  deletedComponent: (component: string) => `${component} is deleted: traffic through it stops.`,
  typeToApply: (network: string) => `Type “${network}” to apply.`,
  blocksLine: (n: number, longest: string | null) =>
    `While this apply runs, applies of the ${n} attached deployments wait${longest ? ` (${longest})` : ''}.`,
  blocksLineUnknown: 'While this apply runs, applies of attached deployments wait.',
  applySubmit: 'Apply',
  // Components (plan rows)
  component: {
    attachment: (deployment: string, provider: string, region: string) =>
      `${deployment} · ${provider} / ${region}`,
    region: (provider: string, region: string) => `${provider} · ${region}`,
  },
  // Runs tab (6.15.11)
  queuedAndRunning: 'Queued and running',
  history: 'History',
  noRuns: 'No runs yet.',
  noActiveRuns: 'Nothing queued or running.',
  runColumns: {
    run: 'Run',
    status: 'Status',
    triggeredBy: 'Triggered by',
    version: 'Version',
    started: 'Started',
    duration: 'Duration',
    changes: 'Changes',
    waiting: 'Waiting for',
  },
  queuedAfter: 'queued after',
  previousRun: 'the previous run',
  queued: 'Queued',
  // Phone (6.15.19)
  groupHeader: (label: string, count: number) => `${label} · ${count}`,
  groupProvider: (provider: string, region: string) => `${provider} · ${region}`,
};
